<template>
    <div class="home-container">
      <div class="hero-section">
        <h2 class="text-3xl font-bold mb-3">Jelajahi Dunia Kata Tanpa Batas</h2>
        <p class="lead mx-auto" style="max-width: 800px">Ribuan cerita menunggu untuk ditemukan di Perpustakaan HobiBaca. Dengan biaya langganan Rp50.000/bulan, dapatkan akses ke semua koleksi buku kami. Bergabunglah sekarang dan biarkan imajinasi Anda mengembara!</p>
        <button class="subscription-button" @click="handleSubscribe">Langganan Sekarang</button>
      </div>

      <!-- Filter Section -->
      <div class="category-dropdown">
        <select v-model="selectedCategoryType" @change="fetchSubCategories" class="modern-select" id="categoryType">
          <option value="all">Semua Buku {{ formatCount(totalCounts.all) }}</option>
          <option value="fiction">Buku Fiksi {{ formatCount(totalCounts.fiction) }}</option>
          <option value="non-fiction">Buku Non-Fiksi {{ formatCount(totalCounts.non_fiction) }}</option>
        </select>
        
        <select v-if="selectedCategoryType !== 'all'" v-model="selectedSubCategory" @change="fetchSubSubCategories" class="modern-select" id="subCategory">
          <option value="">Semua {{ selectedCategoryType === 'fiction' ? 'Fiksi' : 'Non-Fiksi' }}</option>
          <option v-for="category in filteredCategories" :key="category.id" :value="category.id">
            {{ category.name }} 
          </option>
        </select>
        
        <select v-if="selectedSubCategory" v-model="selectedSubSubCategory" @change="filterBooks" class="modern-select" id="subSubCategory">
          <option value="">Semua {{ getSelectedSubCategoryName }}</option>
          <option v-for="subCategory in filteredSubSubCategories" :key="subCategory.id" :value="subCategory.id">
            {{ subCategory.name }} {{ formatCount(bookCounts[subCategory.id]) }}
          </option>
        </select>
      </div>

      <!-- Auth Modal -->
      <div class="modal-overlay" v-if="showLoginModal" @click.self="closeLoginModal">
        <div class="modal-content">
          <div class="modal-header">
            <h2>{{ isLoginMode ? 'Login untuk Berlangganan' : 'Daftar untuk Berlangganan' }}</h2>
            <button class="close-button" @click="closeLoginModal">&times;</button>
          </div>
          <div class="modal-body">
            <LoginForm 
              v-if="isLoginMode"
              :redirectHome="false"
              @login-success="handleLoginSuccess"
            />
            <RegisterForm
              v-else
              :redirectAfterRegister="null"
              @register-success="handleRegisterSuccess"
            />
            <div class="auth-divider">
              <span>atau</span>
            </div>
            <p class="auth-switch">
              {{ isLoginMode ? 'Belum punya akun?' : 'Sudah punya akun?' }}
              <a href="#" @click.prevent="toggleAuthMode">
                {{ isLoginMode ? 'Daftar sekarang' : 'Login di sini' }}
              </a>
            </p>
          </div>
        </div>
      </div>

      <div id="book-list-container">
        <BookList 
          ref="bookList" 
          :limit="15" 
          :category="selectedSubSubCategory || selectedSubCategory || selectedCategoryType" 
        />
      </div>
    </div>
</template>

<script>
import BookList from '@/components/BookList.vue';
import { useHead } from '@unhead/vue';
import axios from 'axios';
import LoginForm from '@/components/LoginForm.vue';
import RegisterForm from '@/components/RegisterForm.vue';

export default {
  name: 'Home',
  components: {
    BookList,
    LoginForm,
    RegisterForm
  },
  data() {
    return {
      categories: [],
      selectedCategoryType: 'all',
      selectedSubCategory: '',
      selectedSubSubCategory: '',
      filteredCategories: [],
      filteredSubSubCategories: [],
      bookCounts: {},
      totalCounts: {
        all: 0,
        fiction: 0,
        non_fiction: 0
      },
      showLoginModal: false,
      isLoginMode: true
    };
  },
  async created() {
    await this.fetchCategories();
    await this.fetchBookCounts();
    await this.fetchTotalCounts();
    await this.fetchSubCategories();
  },
  beforeUnmount() {
    // Ensure to clean up any lingering references to BookList
    if (this.$refs.bookList) {
      this.$refs.bookList = null;
    }
  },
  methods: {
    async fetchCategories() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/book-categories/`);
        this.categories = response.data;
      } catch (error) {
        console.error('Failed to fetch categories', error);
      }
    },
    async fetchBookCounts() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/book-categories/book-counts/`);
        this.bookCounts = response.data;
      } catch (error) {
        console.error('Failed to fetch book counts', error);
      }
    },
    async fetchTotalCounts() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/book-categories/total-counts/`);
        this.totalCounts = response.data;
      } catch (error) {
        console.error('Failed to fetch total counts', error);
      }
    },
    formatCount(count) {
      if (!count) return '(0)';
      if (count >= 1000) {
        return `(${(count/1000).toFixed(1)}K)`;
      }
      return `(${count})`;
    },
    async fetchSubCategories() {
      try {
        // Reset state terlebih dahulu
        this.selectedSubCategory = '';
        this.selectedSubSubCategory = '';
        this.filteredSubSubCategories = [];
        
        if (this.selectedCategoryType === 'all') {
          this.filteredCategories = [];
          this.filterBooks();
          return;
        }

        let url = `${process.env.VUE_APP_API_BASE_URL}/api/book-categories/`;
        if (this.selectedCategoryType === 'fiction') {
          url += 'fiction/';
        } else if (this.selectedCategoryType === 'non-fiction') {
          url += 'non-fiction/';
        }
        const response = await axios.get(url);
        this.filteredCategories = response.data;
        this.filterBooks();
      } catch (error) {
        console.error('Failed to fetch subcategories', error);
        this.filteredCategories = [];
        this.filterBooks();
      }
    },
    async fetchSubSubCategories() {
      try {
        // Reset sub-sub kategori terlebih dahulu
        this.selectedSubSubCategory = '';
        
        if (this.selectedSubCategory) {
          const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/book-categories/${this.selectedSubCategory}/subcategories/`);
          this.filteredSubSubCategories = response.data;
        } else {
          this.filteredSubSubCategories = [];
        }
        this.filterBooks();
      } catch (error) {
        console.error('Failed to fetch sub-subcategories', error);
        this.filteredSubSubCategories = [];
        this.filterBooks();
      }
    },
    filterBooks() {
      // Reset pagination dan fetch buku baru
      let category = this.selectedSubSubCategory || this.selectedSubCategory || this.selectedCategoryType;
      if (this.selectedSubCategory && this.filteredSubSubCategories.length > 0) {
        // Jika subCategory memiliki child, gunakan subCategory sebagai kategori
        category = this.selectedSubCategory;
      }
      if (this.$refs.bookList) {
        this.$refs.bookList.fetchBooks(true, category);
      }
    },
    handleSubscribe() {
      const token = localStorage.getItem('token');
      
      if (!token) {
        this.showLoginModal = true;
        this.isLoginMode = true;
        return;
      }

      // Jika sudah login, redirect ke halaman langganan
      this.$router.push('/langganan');
    },
    handleLoginSuccess() {
      this.showLoginModal = false;
      // Redirect ke halaman langganan setelah login berhasil
      this.$router.push('/langganan');
    },
    handleRegisterSuccess(data) {
      if (data && data.token) {
        localStorage.setItem('token', data.token);
        this.showLoginModal = false;
        // Refresh halaman dan redirect ke langganan
        window.location.href = '/langganan';
      }
    },
    closeLoginModal() {
      this.showLoginModal = false;
      this.isLoginMode = true; // Reset ke mode login saat modal ditutup
    },
    toggleAuthMode() {
      this.isLoginMode = !this.isLoginMode;
    }
  },
  computed: {
    getSelectedSubCategoryName() {
      const category = this.filteredCategories.find(cat => cat.id === parseInt(this.selectedSubCategory));
      return category ? category.name : '';
    },
    fictionCount() {
      return this.categories
        .filter(cat => cat.category_type === 'fiction')
        .reduce((sum, cat) => sum + (this.bookCounts[cat.id] || 0), 0);
    },
    nonFictionCount() {
      return this.categories
        .filter(cat => cat.category_type === 'non-fiction')
        .reduce((sum, cat) => sum + (this.bookCounts[cat.id] || 0), 0);
    }
  },
  setup() {
    useHead({
      title: 'HobiBaca - Baca Buku Online',
      meta: [
        { 
          name: 'description', 
          content: 'Temukan buku-buku menarik dan mulailah membaca! Bab 1 semua buku bisa dibaca gratis. Daftar gratis untuk baca bab 2. Langganan Rp50.000 per bulan untuk akses ke semua koleksi buku.'
        },
        { 
          name: 'keywords', 
          content: 'HobiBaca, buku, membaca, baca buku online' 
        },
      ],
    });
  },
};
</script>

<style scoped>
.home-container {
  text-align: center;
  width: 100%;
  position: relative;
  z-index: 10;
  overflow-y: auto;
  height: 100%;
}

.hero-section {
  background-color: rgba(255, 255, 255, 0.5);
  padding: 2.5rem 2rem;
  width: 100%;
  margin: 0 0 2rem 0;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  top: 0;
}

.hero-section h2 {
  font-size: 2.5rem;
  margin-bottom: 1rem;
}

.hero-section p {
  font-size: 1.2rem;
  line-height: 1.8;
  margin-bottom: 1.5rem;
}

.subscription-button {
  background-color: #3b82f6;
  color: white;
  font-weight: bold;
  font-size: 1.1rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 30px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.2s;
  box-shadow: 0 4px 6px rgba(59, 130, 246, 0.2);
}

.subscription-button:hover {
  background-color: #2563eb;
  transform: translateY(-2px);
}

.category-dropdown {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;
}

.modern-select {
  width: 100%;
  max-width: 300px;
  padding: 12px 20px;
  font-size: 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background-color: white;
  color: #333;
  cursor: pointer;
  transition: all 0.3s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1em;
}

.modern-select:hover {
  border-color: #3b82f6;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
}

.modern-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 2px 12px rgba(59, 130, 246, 0.2);
}

#book-list-container {
  width: 100%;
  position: relative;
  z-index: 20;
}

@media (max-width: 768px) {
  .hero-section {
    padding: 2rem 1rem;
  }
  
  .hero-section h2 {
    font-size: 2rem;
  }
  
  .hero-section p {
    font-size: 1rem;
  }
  
  .modern-select {
    max-width: 100%;
  }
}

.filter-section {
  margin: 2rem auto;
  max-width: 1200px;
  padding: 0 1rem;
}

.filter-group {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.filter-item {
  flex: 1;
  min-width: 200px;
  max-width: 300px;
}

.modern-select {
  width: 100%;
  padding: 12px 20px;
  font-size: 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background-color: white;
  color: #333;
  cursor: pointer;
  transition: all 0.3s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1em;
}

.modern-select:hover {
  border-color: #2196F3;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.1);
}

.modern-select:focus {
  outline: none;
  border-color: #2196F3;
  box-shadow: 0 2px 12px rgba(33, 150, 243, 0.2);
}

.modern-select option {
  padding: 12px;
  color: #333;
  display: flex;
  justify-content: space-between;
}

.modern-select option:hover {
  background-color: #f5f5f5;
}

/* Animasi transisi untuk filter items */
.filter-item {
  opacity: 1;
  transform: translateY(0);
  transition: all 0.3s ease;
}

.filter-item-enter-active, .filter-item-leave-active {
  transition: all 0.3s ease;
}

.filter-item-enter-from, .filter-item-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Responsif untuk layar kecil */
@media (max-width: 768px) {
  .filter-group {
    flex-direction: column;
    align-items: center;
  }

  .filter-item {
    width: 100%;
    max-width: 100%;
  }

  .modern-select {
    font-size: 0.9rem;
    padding: 10px 16px;
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  position: relative;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #1f2937;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
}

.close-button:hover {
  color: #1f2937;
}

.auth-divider {
  text-align: center;
  margin: 1.5rem 0;
  position: relative;
}

.auth-divider::before,
.auth-divider::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 45%;
  height: 1px;
  background-color: #e5e7eb;
}

.auth-divider::before {
  left: 0;
}

.auth-divider::after {
  right: 0;
}

.auth-divider span {
  background-color: white;
  padding: 0 1rem;
  color: #6b7280;
  font-size: 0.9rem;
}

.auth-switch {
  text-align: center;
  margin-top: 1rem;
  color: #4b5563;
  font-size: 0.95rem;
}

.auth-switch a {
  color: #488e71;
  text-decoration: none;
  font-weight: 500;
  margin-left: 0.5rem;
}

.auth-switch a:hover {
  color: #3a7259;
  text-decoration: underline;
}

.modal-body {
  padding: 1rem 0;
}
</style>